import React, { useEffect } from 'react'

import { InboxApi } from 'conversations/api'
import { DriftScrollbars } from 'conversations/components/DriftScrollbars'
import { LazyRender } from 'conversations/components/LazyRender'
import MobileTooltipQR from 'conversations/components/MobileTooltipQR'
import { BadgeCountsSelector } from 'conversations/components/NewChatSidebar/ConversationListHeader'
import { Section } from 'conversations/components/NewChatSidebar/ConversationSection'
import CreateNewSection from 'conversations/components/NewChatSidebar/CreateNewSection'
import { SidebarHeader } from 'conversations/components/NewChatSidebar/SidebarHeader'
import { SidebarPopoverMenuItems } from 'conversations/components/NewChatSidebar/SidebarPopoverMenuItems'
import useBadgeCounts from 'conversations/hooks/useBadgeCounts'
import { useSections } from 'conversations/hooks/useSections'
import { getConversationSidebarModal } from 'conversations/modules/user/sections/selectors'
import { getActiveInbox, getCurrentUserIsBot } from 'conversations/modules/user/selectors'
import { getAppServices } from 'conversations/services'
import { lazyRetry } from 'conversations/utils/rendering/LazyRetryRender'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toggleCreateConvoSectionModal } from 'shared/store/actions/user/sections'
import { useDebouncedCallback } from 'use-debounce'

import { RootState } from 'typesafe-actions'

import originalFavicon from './bolt.png'
import badgeFavicon from './bolt-badge.png'

import styles from './styles.module.css'

async function backfillSections(): Promise<void> {
  await InboxApi.post('/sections/default/backfill')
}

const ConversationSectionModal = lazyRetry(
  () => import('conversations/components/NewChatSidebar/ConversationSectionModal')
)

const NewChatSidebar: React.FC = (): JSX.Element | null => {
  const dispatch = useDispatch()

  const isBot = useSelector<RootState, boolean>(getCurrentUserIsBot)
  const location = useLocation()
  const isOnProspectorPage = location.pathname.includes('/prospector/')
  const shouldHideChatSidebar = isOnProspectorPage

  const sectionConfigs = useSections()
  const myConversationBadgeCounts: BadgeCountsSelector = useBadgeCounts()
  const { total: badgeTotal } = myConversationBadgeCounts

  const changeFavIcon = (icon: string): void => {
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") || document.createElement('link')

    link.type = 'image/png'
    link.rel = 'shortcut icon'
    link.href = icon
    document.getElementsByTagName('head')[0].appendChild(link)
  }

  const debounceBadging = useDebouncedCallback(() => {
    const basicTitle = 'Drift | Conversations'
    if (!isOnProspectorPage) {
      document.title = badgeTotal > 0 ? `(${badgeTotal}) ${basicTitle}` : basicTitle
      badgeTotal > 0 ? changeFavIcon(badgeFavicon) : changeFavIcon(originalFavicon)
      getAppServices().badges.showBadgeIconNumber(badgeTotal).then()
    }
  }, 750)

  useEffect(() => {
    debounceBadging()
  }, [badgeTotal, debounceBadging])

  useEffect(() => {
    isBot && backfillSections()
  }, [isBot])

  const activeInbox = useSelector(getActiveInbox)

  const sectionModal = useSelector(getConversationSidebarModal)

  if (shouldHideChatSidebar) {
    return null
  }

  return (
    <>
      <div className={styles.chatSidebar}>
        <SidebarHeader />
        <MobileTooltipQR>
          <DriftScrollbars data-ocweb-id="ConversationSidebar" color="dark">
            {sectionConfigs.map((sectionConfig) => (
              <Section
                sectionConfig={sectionConfig}
                inboxId={activeInbox?.id}
                key={sectionConfig.id}
              />
            ))}
            <CreateNewSection setModalOpen={() => dispatch(toggleCreateConvoSectionModal())} />
            <SidebarPopoverMenuItems />
          </DriftScrollbars>
        </MobileTooltipQR>

        {sectionModal.open && (
          <LazyRender>
            <ConversationSectionModal
              open={true}
              onCloseModal={() => dispatch(toggleCreateConvoSectionModal())}
              type={sectionModal.modalType}
              sectionID={sectionModal.uuid}
            />
          </LazyRender>
        )}
      </div>
    </>
  )
}

export default NewChatSidebar
